import { useMemo } from "react";

import type { TareBridge } from "../internal/NativeModulesTypes";
import { useTare } from "../internal/useTare";

export interface ProctorSpecification {
  name: string;
  buckets: Record<string, number>;
}

export interface ProctorAllocation {
  testName: string;
  value: number;
  payload: string | null;
}

export interface JSTProctorApi {
  getProctorAllocation(test: ProctorSpecification): Promise<ProctorAllocation | null>;
  getProctorAllocations(
    tests: ProctorSpecification[],
  ): Promise<Record<string, ProctorAllocation> | null>;
  getFullLoggingString(): Promise<string | null>;
  getFullLoggingStringSync(): string | null;
}

export const createJSTProctor = (getBridge: () => TareBridge | undefined): JSTProctorApi => ({
  async getProctorAllocation(test) {
    const bridge = getBridge();
    return bridge?.getProctorAllocation?.(test.name) ?? null;
  },
  async getProctorAllocations(tests) {
    const bridge = getBridge();
    return bridge?.getProctorAllocations?.(tests.map((test) => test.name)) ?? null;
  },
  async getFullLoggingString() {
    const bridge = getBridge();
    return bridge?.getProctorLoggingString?.() ?? null;
  },
  getFullLoggingStringSync() {
    const bridge = getBridge();
    return typeof bridge?.getProctorLoggingStringSync === "function"
      ? bridge.getProctorLoggingStringSync()
      : null;
  },
});

export function useJSTProctor(): JSTProctorApi {
  const tareBridge = useTare();
  return useMemo(() => createJSTProctor(() => tareBridge), [tareBridge]);
}

export default useJSTProctor;
